/*
 * @Author: Zhouqi
 * @Date: 2025-12-10 10:20:56
 * @LastEditors: Ryan
 * @LastEditTime: 2026-02-03
 */
import dynamic from 'next/dynamic';
import React from 'react';

import ImageEnhanceSkeleton from '@/3.0/image-enhance/_components/image-enhance-skeleton';
import { Introduction } from '@/components/container';
import DelayedRender from '@/components/DelayRender';
import Footer from '@/components/Footer';
import Layout from '@/components/layout';
import SEOHead from '@/landing/components/SEOHead';
import { createSoftwareAppSchema } from '@/landing/utils/structuredData';

// 预加载首屏图片资源（第一组对比图）
const PRELOAD_IMAGES = [
  'https://assets.falcocut.ai/ai-image-enhancer/jewelry-image-before.webp',
  'https://assets.falcocut.ai/ai-image-enhancer/jewelry-image-after.webp',
];

// 动态加载完整的 ImageEnhance
const ImageEnhance = dynamic(() => import('@/3.0/image-enhance/entry'), {
  ssr: false,
  loading: () => (
    <DelayedRender>
      <ImageEnhanceSkeleton />
    </DelayedRender>
  ),
});

// 动态加载 SEO 内容
const SEOSections = dynamic(() => import('@/3.0/image-enhance/seo'), {
  ssr: true,
});

// SEO 常量
const PAGE_TITLE = 'AI Image Enhancer - Upscale & Enhance Photos Online | FalcoCut';
const META_DESCRIPTION =
  'Enhance and upscale your images with AI. Improve resolution, sharpen details, restore old photos, and fix blurry images instantly. Free online image enhancement tool with professional results.';
const CURRENT_URL = 'https://falcocut.ai/ai-image-enhancer';

// 结构化数据
const structuredData = createSoftwareAppSchema({
  name: 'FalcoCut AI Image Enhancer',
  description: META_DESCRIPTION,
  url: CURRENT_URL,
  ratingValue: '4.9',
  ratingCount: '3200',
  featureList: [
    'AI-powered image upscaling',
    'Photo quality enhancement',
    'Old photo restoration',
    'Blur and noise reduction',
    'Face enhancement',
    'Batch processing support',
  ],
});

const ImageEnhancerPage: React.FC & {
  getLayout?: (page: React.ReactElement) => React.ReactNode;
  getExtraContent?: () => React.ReactNode;
} = () => {
  return (
    <>
      <SEOHead
        title={PAGE_TITLE}
        description={META_DESCRIPTION}
        url={CURRENT_URL}
        imageAlt="FalcoCut AI Image Enhancer"
        publishedTime="2025-12-10"
        modifiedTime="2026-02-03"
        structuredData={structuredData}
        preloadImages={PRELOAD_IMAGES}
      />

      {/* 客户端动态加载完整组件（保留骨架屏） */}
      <ImageEnhance />
    </>
  );
};

// 定义 getLayout 为直接返回页面本身，使用 Layout
ImageEnhancerPage.getLayout = (page: React.ReactElement) => <Layout>{page}</Layout>;

// 定义 SEO 部分和 Footer 作为额外内容，在 Layout 外渲染
ImageEnhancerPage.getExtraContent = () => {
  return (
    <Introduction>
      <SEOSections />
      <Footer />
    </Introduction>
  );
};

export const getStaticProps = async () => {
  return {
    props: {},
  };
};

export default ImageEnhancerPage;
